import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    Signal,
    signal,
    viewChild,
    WritableSignal,
} from '@angular/core';

import { ActiveFilterDropdownComponent } from '@connect-services/client/components/active-filter-content/active-filter-dropdown/active-filter-dropdown.component';
import { ActiveFilterItemComponent } from '@connect-services/client/components/active-filter-content/active-filter-item/active-filter-item.component';
import { MapFilterService } from '@connect-services/client/services/map-filter.service';
import { MapFilter } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

const BUTTON_WIDTH = 8.5;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ActiveFilterDropdownComponent, ActiveFilterItemComponent, RessourcesModule],
    selector: 'app-active-filter-content',
    templateUrl: './active-filter-content.component.html',
})
export class ActiveFilterContentComponent implements OnInit, OnDestroy {
    public static readonly DROPDOWN_WIDTH_REM_WITH_MARGIN = 9;
    public readonly container = viewChild.required<ElementRef>('container');
    public readonly currentWidth: WritableSignal<number> = signal(0);

    protected readonly activeFilters: Signal<MapFilter[]> = computed<MapFilter[]>(() => {
        return this.mapFilterService
            .filters()
            .filter((filter: MapFilter) => this.mapFilterService.selectedFilters().includes(filter.id));
    });
    protected readonly activeFiltersInContentBar: Signal<MapFilter[]> = computed(() => {
        const filters: MapFilter[] = this.activeFilters();

        return filters.slice(0, this.displayableFiltersCount());
    });
    protected readonly activeFiltersInDropdown: Signal<MapFilter[]> = computed(() => {
        const filters: MapFilter[] = this.activeFilters();

        return filters.slice(this.displayableFiltersCount());
    });
    protected readonly mapFilterService: MapFilterService = inject(MapFilterService);

    private availableSpaceObserver?: ResizeObserver;
    private readonly displayableFiltersCount: Signal<number> = computed(() => {
        const total: number = this.activeFilters().length;
        const maxWithoutDropdown: number = Math.floor(this.currentWidth() / BUTTON_WIDTH);

        if (total <= maxWithoutDropdown) {
            return total;
        }

        const reservedWidth: number = this.currentWidth() - ActiveFilterContentComponent.DROPDOWN_WIDTH_REM_WITH_MARGIN;

        return Math.max(0, Math.floor(reservedWidth / BUTTON_WIDTH));
    });

    public ngOnDestroy(): void {
        this.availableSpaceObserver?.disconnect();
    }

    public ngOnInit(): void {
        this.availableSpaceObserver = new ResizeObserver((content: ResizeObserverEntry[]) => {
            this.currentWidth.set(this.convertPixelToRem(content[0].contentRect.width));
        });

        this.availableSpaceObserver.observe(this.container().nativeElement);
    }

    private convertPixelToRem(px: number): number {
        return px / 16;
    }
}
